import { lazy, Suspense } from "react";
import { Switch, Route, Router, useLocation } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { EngineerLayout } from "@/components/engineer-layout";
import { ThemeProvider, ThemeToggle } from "@/components/theme-provider";
import { UserProvider, useUser } from "@/components/user-context";
import { FeaturesProvider, useFeatures } from "@/lib/features";
import { isAdmin } from "@/lib/roles";
import { PrivacyProvider } from "@/lib/privacy";
import { PasscodeGate } from "@/components/passcode-gate";
import { Skeleton } from "@/components/ui/skeleton";
import { OfflineBanner } from "@/components/offline-banner";

// Pages — lazy-loaded so each route is a separate JS chunk. Vite splits these
// automatically; the browser only downloads a chunk when that route is first visited.
//
// Phase 12 (UI cutover): legacy RoofOps pages (jobs/quotes/invoicing/payments/
// schedule/clients/recurring-jobs/reports) are no longer routed. The page files
// still exist on disk and will be deleted in PR B; until then we simply do not
// import them here so they are unreachable.
const DashboardPage         = lazy(() => import("@/pages/dashboard"));
const EngineersPage         = lazy(() => import("@/pages/engineers"));
const EngineerVisitsPage       = lazy(() => import("@/pages/engineer-visits"));
const EngineerVisitDetailPage  = lazy(() => import("@/pages/engineer-visit-detail"));
const EngineerVisitInspectPage = lazy(() => import("@/pages/engineer-visit-inspect"));
const LoginPage             = lazy(() => import("@/pages/login"));
const InvitePage            = lazy(() => import("@/pages/invite"));
const NotFound              = lazy(() => import("@/pages/not-found"));
const SettingsPage          = lazy(() => import("@/pages/settings"));
const CustomersPage         = lazy(() => import("@/pages/customers"));
const SitesPage             = lazy(() => import("@/pages/sites"));
const AssetsPage            = lazy(() => import("@/pages/assets"));
const DefectsPage           = lazy(() => import("@/pages/defects"));
const PortalUsersPage       = lazy(() => import("@/pages/portal-users"));

// New fire-safety module placeholders (full UIs land in later PRs)
const VisitsListPage   = lazy(() => import("@/pages/visits"));
const CalendarPage     = lazy(() => import("@/pages/module-placeholder").then(m => ({ default: m.CalendarPage })));
const CertificatesPage = lazy(() => import("@/pages/module-placeholder").then(m => ({ default: m.CertificatesPage })));
const FrasPage         = lazy(() => import("@/pages/module-placeholder").then(m => ({ default: m.FrasPage })));
const RecurringPage    = lazy(() => import("@/pages/module-placeholder").then(m => ({ default: m.RecurringPage })));
const ContractsPage    = lazy(() => import("@/pages/module-placeholder").then(m => ({ default: m.ContractsPage })));

// Legacy-path → new-path redirects (instant client-side navigations)
import {
  RedirectJobsToVisits,
  RedirectScheduleToVisits,
  RedirectClientsToCustomers,
  RedirectRecurringJobsToRecurring,
} from "@/pages/redirect";

function PageFallback() {
  return (
    <div className="p-6 space-y-3">
      <Skeleton className="h-8 w-56 rounded" />
      <Skeleton className="h-4 w-full rounded" />
      <Skeleton className="h-4 w-3/4 rounded" />
    </div>
  );
}

// Admin-only paths that engineers must never land on.
// Prefix match: any path equal to or descended from one of these is admin-only.
// /visits is engineer-routable so it is intentionally NOT in this list.
// /jobs* used to be a mix (engineer job-detail used /jobs/:id) but is now
// fully legacy — admins redirect to /visits, engineers fall through to NotFound.
const ADMIN_ONLY_PATHS = [
  // Legacy paths (engineers shouldn't see these even via stale bookmarks)
  "/jobs", "/quotes", "/invoicing", "/payments", "/schedule", "/clients",
  "/recurring-jobs", "/reports", "/my-jobs",
  // Current admin paths
  "/engineers", "/customers", "/sites", "/assets", "/defects", "/settings",
  "/portal-users",
  // New fire-safety modules (Phase 12)
  "/calendar", "/certificates", "/fras", "/recurring", "/contracts",
];

// Routes rendered inside the hash router context
function AppRoutes() {
  const { currentUser } = useUser();
  const features = useFeatures();
  const [location, navigate] = useLocation();

  // First-admin invite redemption is a public path — handled before the auth
  // gate so a freshly-provisioned tenant with zero real users can be
  // bootstrapped from the email link. The page also drives loginWithToken()
  // on success, so the user lands signed in.
  if (!currentUser && location.startsWith("/invite")) return (
    <Suspense fallback={<PageFallback />}>
      <InvitePage />
    </Suspense>
  );

  if (!currentUser) return (
    <Suspense fallback={<PageFallback />}>
      <LoginPage />
    </Suspense>
  );

  if (currentUser.role === "engineer") {
    // Redirect away from any admin-only path immediately.
    // Prefix check covers entire subtrees (e.g. /quotes/123).
    // Exact check covers paths like /jobs (list) and /jobs/new.
    const isAdminPath =
      ADMIN_ONLY_PATHS.some(p => location === p || location.startsWith(p + "/"));
    if (isAdminPath) {
      navigate("/", { replace: true });
      return null;
    }

    return (
      <Suspense fallback={<PageFallback />}>
        <Switch>
          <Route path="/" component={EngineerVisitsPage} />
          <Route path="/visits" component={EngineerVisitsPage} />
          <Route path="/visits/:id/inspect" component={EngineerVisitInspectPage} />
          <Route path="/visits/:id" component={EngineerVisitDetailPage} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    );
  }

  // Admin layout — fire-safety operations console.
  //
  // Compliance lifecycle modules (assets, defects, certificates, FRAs, contracts,
  // recurring schedules) are gated behind feature flags so we can ship the IA
  // and stand up implementations incrementally.
  return (
    <Suspense fallback={<PageFallback />}>
      <Switch>
        {/* Overview */}
        <Route path="/" component={DashboardPage} />

        {/* Compliance — customers/sites/assets are foundational, always on for admins */}
        {isAdmin(currentUser) && <Route path="/customers"  component={CustomersPage} />}
        {isAdmin(currentUser) && <Route path="/sites"      component={SitesPage} />}
        {isAdmin(currentUser) && <Route path="/sites/:id"  component={SitesPage} />}
        {isAdmin(currentUser) && features.assets  && <Route path="/assets"  component={AssetsPage} />}
        {isAdmin(currentUser) && features.defects && <Route path="/defects" component={DefectsPage} />}

        {/* Visits — admin list + detail. Detail is shared with the engineer
            view so the same screen drives both roles. */}
        <Route path="/visits"     component={VisitsListPage} />
        <Route path="/visits/:id" component={EngineerVisitDetailPage} />

        {/* Feature-gated compliance modules (placeholders for now) */}
        {features.schedule            && <Route path="/calendar"     component={CalendarPage} />}
        {features.certificates        && <Route path="/certificates" component={CertificatesPage} />}
        {features.riskAssessments     && <Route path="/fras"         component={FrasPage} />}
        {features.recurringSchedules  && <Route path="/recurring"    component={RecurringPage} />}
        {features.contracts           && <Route path="/contracts"    component={ContractsPage} />}

        {/* Commercial */}
        {features.team && <Route path="/engineers" component={EngineersPage} />}

        {/* Portal user management (ADR 0002 — slice 8). Admin-only,
            unconditionally routable so admins can deep-link to it
            from the customer detail page or from a bookmark. */}
        {isAdmin(currentUser) && <Route path="/portal-users" component={PortalUsersPage} />}

        {/* Admin */}
        <Route path="/settings" component={SettingsPage} />

        {/* Legacy-path redirects — keep stale bookmarks/links working.
            /jobs* → /visits, /schedule → /visits, /clients → /customers,
            /recurring-jobs → /recurring. Removed paths (/quotes, /invoicing,
            /payments, /reports) intentionally fall through to NotFound. */}
        <Route path="/jobs"           component={RedirectJobsToVisits} />
        <Route path="/jobs/new"       component={RedirectJobsToVisits} />
        <Route path="/jobs/:id/edit"  component={RedirectJobsToVisits} />
        <Route path="/jobs/:id"       component={RedirectJobsToVisits} />
        <Route path="/schedule"       component={RedirectScheduleToVisits} />
        <Route path="/clients"        component={RedirectClientsToCustomers} />
        <Route path="/recurring-jobs" component={RedirectRecurringJobsToRecurring} />

        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

const sidebarStyle = {
  "--sidebar-width": "15rem",
  "--sidebar-width-icon": "3.5rem",
};

function LayoutWrapper() {
  const { currentUser, hydrating } = useUser();

  // While re-validating a stored token, show a minimal loading state
  // rather than flashing the login screen
  if (hydrating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-3 w-64">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-6 w-3/4 mx-auto rounded" />
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <AppRoutes />;
  }

  // Engineers get a mobile-first bottom-nav layout — no sidebar
  if (currentUser.role === "engineer") {
    return (
      <EngineerLayout>
        <AppRoutes />
      </EngineerLayout>
    );
  }

  // Admins get the full sidebar layout
  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-hidden">
        <AppSidebar />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center justify-between px-4 py-2 border-b border-border bg-background h-12 shrink-0 z-10">
            <SidebarTrigger data-testid="button-sidebar-toggle" className="-ml-1" />
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground hidden sm:block">
                {currentUser.name}
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground capitalize">{currentUser.role}</span>
              </span>
              <ThemeToggle />
            </div>
          </header>
          <OfflineBanner />
          <main className="flex-1 overflow-auto">
            <AppRoutes />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          {/*
           * ACCESS GATE — demo protection.
           * Controlled by ACCESS_PASSCODE env var in Vercel.
           * To remove when going live: delete the ACCESS_PASSCODE env var.
           * The <PasscodeGate> wrapper can then also be removed from here,
           * but it is harmless to leave in — if ACCESS_PASSCODE is unset,
           * the gate renders nothing and passes straight through.
           */}
          <PasscodeGate>
            <UserProvider>
              <FeaturesProvider>
                <PrivacyProvider>
                  <Router hook={useHashLocation}>
                    <LayoutWrapper />
                    <Toaster />
                  </Router>
                </PrivacyProvider>
              </FeaturesProvider>
            </UserProvider>
          </PasscodeGate>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
